import type { Project } from '../../project/types/project';
import { getMaterialProjectName } from '../../project/components/dashboard/materials/getMaterialProjectName';
import type { MaterialData } from '../../project/types/materials';

export type ProjectNameSource = {
  projectId?: string | number | null;
  projectName?: string | null;
  project_name?: string | null;
};

function projectLabelFromSource(p: ProjectNameSource): string {
  const name = p.projectName ?? p.project_name;
  return typeof name === 'string' ? name.trim() : '';
}

/** Build projectId → name map from ProjectList and/or Analysis summary projects. */
export function buildProjectNameByIdMap(
  sources: Array<ProjectNameSource | Project | null | undefined>
): Map<string, string> {
  const map = new Map<string, string>();
  for (const source of sources) {
    if (!source) continue;
    const id = source.projectId;
    const name = projectLabelFromSource(source as ProjectNameSource);
    if (id == null || id === '' || !name) continue;
    map.set(String(id), name);
  }
  return map;
}

export function getProjectIdFromMaterialRow(
  row: Record<string, unknown>,
  projectList: ProjectNameSource[] = []
): string | null {
  const details = row.details as Record<string, unknown> | undefined;
  const nestedProject =
    row.project && typeof row.project === 'object'
      ? (row.project as Record<string, unknown>)
      : undefined;

  const direct =
    row.projectId ??
    row.project_id ??
    nestedProject?.projectId ??
    nestedProject?.project_id ??
    details?.projectId ??
    details?.project_id;

  if (direct != null && direct !== '') {
    return String(direct);
  }

  const name = getMaterialProjectName(row);
  if (name && projectList.length) {
    const match = projectList.find((p) => {
      const label = projectLabelFromSource(p);
      return label === name || label.toLowerCase() === name.toLowerCase();
    });
    if (match?.projectId != null && match.projectId !== '') {
      return String(match.projectId);
    }
  }

  return null;
}

/**
 * Analysis Pipe Dashboard only: prefer labels on the material row; if missing,
 * resolve `projectId` against a projectId→name map (ProjectList + summary).
 */
export function resolveAnalysisMaterialProjectName(
  row: Record<string, unknown>,
  projectListOrMap: ProjectNameSource[] | Map<string, string>
): string {
  const labeled = getMaterialProjectName(row);
  if (labeled) return labeled;

  const nameById =
    projectListOrMap instanceof Map
      ? projectListOrMap
      : buildProjectNameByIdMap(projectListOrMap);

  const projectId = getProjectIdFromMaterialRow(
    row,
    projectListOrMap instanceof Map ? [] : projectListOrMap
  );
  if (!projectId) return '';

  return nameById.get(projectId)?.trim() || '';
}

/** Stamp `projectName` onto rows so table + filters show names (Analysis only). */
export function withResolvedAnalysisProjectNames(
  materials: MaterialData[],
  projectListOrMap: ProjectNameSource[] | Map<string, string>
): MaterialData[] {
  if (!materials.length) return materials;

  const nameById =
    projectListOrMap instanceof Map
      ? projectListOrMap
      : buildProjectNameByIdMap(projectListOrMap);

  if (nameById.size === 0) return materials;

  return materials.map((material) => {
    const row = material as unknown as Record<string, unknown>;
    if (getMaterialProjectName(row)) return material;

    const resolved = resolveAnalysisMaterialProjectName(row, nameById);
    if (!resolved) return material;

    return { ...material, projectName: resolved };
  });
}

export function findPipeSetupForMaterial(
  pipeSizesData: Array<Record<string, unknown>>,
  row: Record<string, unknown>
): Record<string, unknown> | null {
  if (!pipeSizesData.length) return null;

  const details = row.details as Record<string, unknown> | undefined;
  const pipeIdCandidates = [row.pipeId, details?.pipePipeId, details?.pipeId].filter(
    (v) => v != null && v !== ''
  );

  for (const id of pipeIdCandidates) {
    const match = pipeSizesData.find((p) => String(p.pipeId) === String(id));
    if (match) return match;
  }

  return pipeSizesData[0] ?? null;
}

export function pipeDetailsBelongToProject(
  pipeDetails: Record<string, unknown> | undefined,
  projectId: string | number
): boolean {
  if (!pipeDetails || Object.keys(pipeDetails).length === 0) return false;
  return Object.values(pipeDetails).some((entry) => {
    if (!entry || typeof entry !== 'object') return false;
    const setup = entry as Record<string, unknown>;
    return String(setup.projectId) === String(projectId);
  });
}
